/**
 * Service to track quiz attempts for free users
 * Uses localStorage to persist attempts across sessions
 */

const QUIZ_ATTEMPTS_KEY = 'learn2skill_quiz_attempts';

interface QuizAttempt {
  courseId: string;
  timestamp: string;
  score?: number;
}

/**
 * Get all quiz attempts for free users
 */
export const getQuizAttempts = (): QuizAttempt[] => {
  try {
    const stored = localStorage.getItem(QUIZ_ATTEMPTS_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to get quiz attempts:', error);
    return [];
  }
};

/**
 * Check if a free user has already attempted a quiz for a course
 */
export const hasAttemptedQuiz = (courseId: string): boolean => {
  const attempts = getQuizAttempts();
  return attempts.some((attempt) => attempt.courseId === courseId);
};

/**
 * Record a quiz attempt for a free user
 */
export const recordQuizAttempt = (courseId: string, score?: number): void => {
  try {
    const attempts = getQuizAttempts();
    // Check if attempt already exists
    const existingIndex = attempts.findIndex((attempt) => attempt.courseId === courseId);
    
    if (existingIndex >= 0) {
      // Update existing attempt
      attempts[existingIndex] = {
        courseId,
        timestamp: new Date().toISOString(),
        score,
      };
    } else {
      // Add new attempt
      attempts.push({
        courseId,
        timestamp: new Date().toISOString(),
        score,
      });
    }
    
    localStorage.setItem(QUIZ_ATTEMPTS_KEY, JSON.stringify(attempts));
  } catch (error) {
    console.error('Failed to record quiz attempt:', error);
  }
};

/**
 * Clear all quiz attempts (useful for testing or reset)
 */
export const clearQuizAttempts = (): void => {
  try {
    localStorage.removeItem(QUIZ_ATTEMPTS_KEY);
  } catch (error) {
    console.error('Failed to clear quiz attempts:', error);
  }
};

/**
 * Get the difficulty level for questions based on user authentication status
 */
export const getQuestionDifficultyFilter = (isAuthenticated: boolean): ('easy' | 'medium' | 'hard')[] => {
  if (isAuthenticated) {
    // Logged-in users can access all difficulty levels
    return ['easy', 'medium', 'hard'];
  } else {
    // Free users only get easy and medium questions
    return ['easy', 'medium'];
  }
};

